import { WorkOrder, Environment, WorkOrderEvent } from '../types';

/**
 * Calcula as datas de início e término de cada ambiente
 * considerando técnico e atrasos de ocorrências.
 */
export function calculateEnvironmentSchedule(
  order: WorkOrder,
  events: WorkOrderEvent[]
): Environment[] {
  if (!order.suggestedDate || !order.environments?.length) {
    return order.environments || [];
  }

  const baseDate = new Date(order.suggestedDate);
  baseDate.setHours(0, 0, 0, 0);

  // Filtrar apenas ocorrências com impacto em prazo
  const delayEvents = events.filter(e => e.impactDeadline && e.impactWorkdays);

  // Atraso geral (impacta todos os ambientes)
  const generalDelayDays = delayEvents
    .filter(e => 
      e.scope === 'work_order' || 
      (e.scope as string) === 'general' || 
      (e as any).occurrenceScope === 'general' || 
      (!e.environmentId && !(e as any).environment_id)
    )
    .reduce((sum, e) => sum + (e.impactWorkdays || 0), 0);

  // Atrasos por ambiente específico
  const envDelays: Record<string, number> = {};
  delayEvents
    .filter(e => 
      (e.scope === 'environment' || (e.scope as string) === 'specific') && 
      (e.environmentId || (e as any).environment_id)
    )
    .forEach(e => {
      const envId = (e.environmentId || (e as any).environment_id)!;
      envDelays[envId] = (envDelays[envId] || 0) + (e.impactWorkdays || 0);
    });

  // Agrupar ambientes por técnico
  const byTechnician: Record<string, Environment[]> = {};
  order.environments.forEach(env => {
    const key = env.technicianId || 'unassigned';
    if (!byTechnician[key]) byTechnician[key] = [];
    byTechnician[key].push(env);
  });

  const result: Environment[] = [];

  // Para cada grupo de técnico, calcular sequência
  Object.values(byTechnician).forEach(techEnvs => {
    // Ordenar por campo ordem ou pela posição original
    const sorted = [...techEnvs].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

    let currentDate = new Date(baseDate);
    // Aplicar atraso geral no início
    currentDate.setDate(currentDate.getDate() + generalDelayDays);

    sorted.forEach(env => {
      const specificDelay = envDelays[env.id] || 0;
      const totalDays = (env.estimatedDays || 1) + specificDelay;

      const startDate = new Date(currentDate);
      const endDate = new Date(currentDate);
      endDate.setDate(endDate.getDate() + totalDays - 1);

      result.push({
        ...env,
        start_date: startDate.toISOString().split('T')[0],
        adjusted_days: totalDays,
      });

      // Próximo ambiente do mesmo técnico começa após este terminar
      currentDate = new Date(endDate);
      currentDate.setDate(currentDate.getDate() + 1);
    });
  });

  return result;
}

/**
 * Calcula a data de término total da OS
 * (maior data de término entre todos os ambientes)
 */
export function calculateOrderEndDate(
  scheduledEnvironments: Environment[]
): Date | null {
  if (!scheduledEnvironments.length) return null;

  let latest: Date | null = null;

  scheduledEnvironments.forEach(env => {
    if (!env.start_date) return;
    const start = new Date(env.start_date);
    const days = env.adjusted_days || env.estimatedDays || 1;
    const end = new Date(start);
    end.setDate(start.getDate() + days - 1);

    if (!latest || end > latest) latest = end;
  });

  return latest;
}
